import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { IMyAssignedTask } from "../../@types/Note";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { PatientNotesTableContent } from "../../@types/Patient";
import { DEFAULT_PAGE_SIZE } from "../../constants/pagination";
import { noteService } from "../../services/noteService";
import { sanitizeHTML } from "../../utils/htmlSanitizer";

function removeTags(str: string) {
  if (str === null || str === "") return "";

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  return str.replace(/(<([^>]+)>)/gi, " ");
}

export function textTruncate(htmlString: string) {
  const str: string = removeTags(htmlString);
  const length = 70;
  const ending = "...";

  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending;
  }
  return str;
}

export function getPatientNotesData(patientId: number) {
  const { data, isFetching, isError, refetch, isRefetching } = useQuery<
    PatientNotesTableContent[]
  >({
    queryKey: [`patient-notes-table-${patientId}`, patientId],
    queryFn: async () => {
      if (!patientId) return [] as PatientNotesTableContent[];

      const { data } = await noteService.getNotes(patientId);

      return data.notes as PatientNotesTableContent[];
    },
  });

  const patientNoteTableData = data as PatientNotesTableContent[];

  return {
    patientNoteTable: patientNoteTableData?.map((noteData) => {
      return {
        ...noteData,
        notes: textTruncate(noteData.notes),
        time: noteData.time,
      } as PatientNotesTableContent;
    }),
    isFetching,
    isError,
    refetch,
    isRefetching,
  };
}

export function getNoteData(noteId: number) {
  const queryClient = useQueryClient();

  const { data, isFetching, isError, refetch, isRefetching } =
    useQuery<PatientNotesTableContent>({
      queryKey: [`note-${noteId}`, noteId],
      queryFn: async () => {
        if (!noteId) return {} as PatientNotesTableContent;

        const { data } = await noteService.getNoteById(noteId);

        return data as PatientNotesTableContent;
      },
    });

  const patientNoteData = data as PatientNotesTableContent;

  const handleRemove = () => {
    // The individual note must be removed so reopening triggers a refetch
    // (the cached copy from before the edit would otherwise win). The list
    // queries are merely invalidated — they refetch in the background while
    // showing existing rows, keeping the table responsive.
    queryClient.removeQueries({ queryKey: [`note-${noteId}`] });
    queryClient.invalidateQueries({ queryKey: ["patient-notes-infinite"] });
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith("patient-notes-table-"),
    });
  };

  return {
    patientNoteData,
    isFetching,
    isError,
    refetch,
    isRefetching,
    remove: handleRemove,
  };
}

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Uses shared htmlSanitizer utility with link security enabled
 *
 * This function adds security attributes (target="_blank", rel="noopener noreferrer")
 * to external links, which is important for notes that may be viewed by different users.
 *
 * For the core sanitization logic, see: src/utils/htmlSanitizer.ts
 */
export const cleanHTML = (str: string) => {
  return sanitizeHTML(str, { securifyLinks: true });
};

type NotesPaginatedResponse = IPaginatedResponse<PatientNotesTableContent[]>;

/**
 * Hook for fetching patient notes with infinite scrolling support.
 * Uses TanStack Query's useInfiniteQuery for paginated data fetching.
 *
 * @param patientId - The patient ID to fetch notes for
 * @param pageSize - Number of items per page (default: 20)
 */
export function useInfiniteNotes(
  patientId: number,
  pageSize: number = DEFAULT_PAGE_SIZE
) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["patient-notes-infinite", patientId, pageSize],
    queryFn: async ({ pageParam }): Promise<NotesPaginatedResponse> => {
      if (!patientId) {
        return {
          result: [],
          currentPage: 1,
          pageSize,
          pages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      }

      const response = await noteService.getNotesPaginated(
        patientId,
        pageParam,
        pageSize
      );

      return response.data as NotesPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: NotesPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single array of notes
  const notes =
    data?.pages.flatMap((page: NotesPaginatedResponse) => {
      const items = page.result || [];
      return items.map((noteData: PatientNotesTableContent) => {
        return {
          ...noteData,
          notes: textTruncate(noteData.notes),
          time: noteData.time,
        } as PatientNotesTableContent;
      });
    }) || [];

  // Get total count from the first page
  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : notes.length;

  return {
    notes,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}

type MyTasksPaginatedResponse = IPaginatedResponse<IMyAssignedTask[]>;

/**
 * Hook for fetching tasks where the current HCP is either the assignee or the creator.
 * Powers the "My tasks" section in the Alerts & tasks sidebar page.
 */
export function useMyAssignedTasks(pageSize: number = DEFAULT_PAGE_SIZE) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: ["my-assigned-tasks-infinite", pageSize],
    queryFn: async ({ pageParam }): Promise<MyTasksPaginatedResponse> => {
      const response = await noteService.getMyAssignedTasks(
        pageParam,
        pageSize
      );
      return response.data as MyTasksPaginatedResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: MyTasksPaginatedResponse) => {
      if (lastPage.hasNextPage) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
  });

  const tasks =
    data?.pages.flatMap((page: MyTasksPaginatedResponse) => {
      const items = page.result || [];
      return items.map((task) => ({
        ...task,
        notes: textTruncate(task.notes),
      }));
    }) || [];

  const totalCount =
    data?.pages[0]?.pages !== undefined
      ? data.pages[0].pages * pageSize
      : tasks.length;

  return {
    tasks,
    totalCount,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isRefetching,
  };
}
